"use client";

import React, { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Phone, ArrowRight, ChevronRight, Camera } from "lucide-react";
import { Link } from "@/components/common/Link";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
interface PestItem {
  id: string;
  name: string;
  description: string;
  details: string;
  image: {
    src: string;
    srcset: string;
    sizes: string;
    alt: string;
  };
}
const pestItems: PestItem[] = [{
  id: "rats-mice",
  name: "Rats & Mice",
  description: "Tamper-proof exterior baiting and subfloor perimeter stations to protect structural cavities.",
  details: "Watch for gnawed electrical wiring, droppings in the subfloor or roof void, and scratching sounds at night. Auckland's mild winters never get cold enough to reduce rodent numbers naturally, so rats and mice seek warm roof voids and subfloors year-round. We treat with tamper-proof exterior bait stations and subfloor/roof void perimeter baiting, seal entry points, and return for follow-up checks.",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1802486/img/pest_rats_mice.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1802486/img/pest_rats_mice.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1802486/img/pest_rats_mice.png-wvc-sizes",
    alt: "Rats and mice pest control close-up"
  }
}, {
  id: "cockroaches",
  name: "Cockroaches",
  description: "Targeted gel baits and perimeter micro-barrier sprays that stop breeding cycles fast.",
  details: "Signs include droppings resembling coffee grounds, a musty odour, and night-time sightings near sinks and drains. Our humid coastal climate accelerates breeding, especially of German cockroaches in kitchens and bathrooms. We use targeted gel baiting plus a perimeter micro-barrier spray to break the breeding cycle without spraying living areas.",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1802486/img/pest_cockroaches.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1802486/img/pest_cockroaches.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1802486/img/pest_cockroaches.png-wvc-sizes",
    alt: "Cockroach pest control inspection"
  }
}, {
  id: "fleas",
  name: "Fleas",
  description: "Dual-action knockdown and insect growth regulator treatments safe for home living zones.",
  details: "Itchy bites around ankles, pets scratching excessively, and small dark specks in carpet fibres are common signs. Warm, humid months let flea eggs and larvae develop rapidly in carpets and lawns. Our dual-action knockdown spray combined with an insect growth regulator stops the life cycle and is safe for pets once dry.",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1802486/img/pest_fleas_macro_closeup.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1802486/img/pest_fleas_macro_closeup.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1802486/img/pest_fleas_macro_closeup.png-wvc-sizes",
    alt: "Close-up macro photo of a single flea on pet fur"
  }
}, {
  id: "bedbugs",
  name: "Bedbugs",
  description: "Intensive multi-stage residual and localized heat applications for complete peace of mind.",
  details: "Look for small blood spots on sheets, itchy bite clusters, and a musty sweet odour near mattress seams. Increased travel and shared accommodation make reintroduction common regardless of season. We apply an intensive multi-stage residual treatment combined with localized heat to reach eggs and adults hidden in seams and frames.",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1802486/img/pest_bedbugs.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1802486/img/pest_bedbugs.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1802486/img/pest_bedbugs.png-wvc-sizes",
    alt: "Bedbug detection and heat treatment"
  }
}, {
  id: "ants",
  name: "Ants",
  description: "Non-repellent transfer sprays carried back to eradicate the entire underground nest.",
  details: "Visible trails along skirting boards and kitchen benches, or small soil mounds near paving, are typical signs. Humid Auckland summers drive rapid colony expansion, especially with Argentine ants along foundations. We use non-repellent transfer sprays that ants carry back to the nest, eliminating the colony at its source.",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1802486/img/pest_ants.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1802486/img/pest_ants.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1802486/img/pest_ants.png-wvc-sizes",
    alt: "Ant colony eradication and control"
  }
}, {
  id: "wasps",
  name: "Wasps",
  description: "Rapid direct nest elimination and preventative perimeter barriers along eaves and decks.",
  details: "Papery nests under eaves or in wall cavities, and increased activity near outdoor entertaining areas in late summer, are key warning signs. Our mild coastal climate extends the active wasp season into autumn. We carry out direct nest elimination plus a preventative perimeter barrier along eaves, fascia and decking to stop new nests forming.",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1802486/img/pest_wasps.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1802486/img/pest_wasps.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1802486/img/pest_wasps.png-wvc-sizes",
    alt: "Wasp nest removal and exterior barrier"
  }
}, {
  id: "spiders",
  name: "Spiders",
  description: "Exterior eave, fence, and window frame webbing treatments that prevent re-entry.",
  details: "Webbing in eaves, window frames and fence lines, along with egg sacs in sheltered corners, indicate spider activity. Year-round mild weather keeps populations active outdoors and around entry points. We treat exterior eaves, fences and window frames to break webbing cycles and deter re-entry.",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1802486/img/pest_spiders.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1802486/img/pest_spiders.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1802486/img/pest_spiders.png-wvc-sizes",
    alt: "Spider prevention and webbing treatment"
  }
}, {
  id: "flies",
  name: "Flies",
  description: "Residual surface applications on outdoor entertaining walls, soffits, and sheltered ceilings.",
  details: "Persistent buzzing around outdoor entertaining areas and small dark specks on walls or ceilings near light fittings are common signs. Warm, humid conditions and outdoor living drive fly pressure over spring and summer. We apply a residual surface treatment to outdoor walls, soffits and sheltered ceiling areas where flies rest.",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1802486/img/pest_flies.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1802486/img/pest_flies.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1802486/img/pest_flies.png-wvc-sizes",
    alt: "Housefly surface treatment for decks and eaves"
  }
}, {
  id: "carpet-beetles",
  name: "Carpet Beetles",
  description: "Targeted natural fiber and baseboard treatments safeguarding floorcoverings and wardrobes.",
  details: "Irregular holes in wool carpets, rugs or clothing, and shed larval skins near wardrobes or skirting, are signs of an active infestation. Humid homes and natural-fibre furnishings provide ideal breeding conditions. We treat natural-fibre areas, baseboards and wardrobes to protect floor coverings and textiles.",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1802486/img/pest_carpet_beetles.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1802486/img/pest_carpet_beetles.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1802486/img/pest_carpet_beetles.png-wvc-sizes",
    alt: "Carpet beetle fabric and wool protection"
  }
}];
export default function Pests() {
  const [selectedPest, setSelectedPest] = useState<PestItem | null>(null);
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.06,
        delayChildren: shouldReduceMotion ? 0 : 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.45,
        ease: "easeOut"
      }
    }
  };
  return <section id="pests-we-treat" data-nav="light" className="relative bg-background py-16 md:py-24 text-foreground">
      <div className="mx-auto max-w-7xl px-5 sm:px-6 lg:px-8">
        {/* Header Block with Pristine Datum Motif */}
        <div className="mx-auto max-w-3xl text-center">
          <div className="inline-flex items-center gap-2 rounded-full border border-primary/20 bg-secondary px-3.5 py-1 text-xs font-semibold uppercase tracking-wider text-secondary-foreground">
            <span>Targeted Protection</span>
          </div>

          <div className="mt-4 flex justify-center">
            <div className="h-[2px] w-9 bg-primary" />
          </div>

          <h2 className="mt-4 text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
            Pests we treat across Auckland homes
          </h2>

          <p className="mt-4 text-base leading-relaxed text-muted-foreground sm:text-lg">
            Every property faces different seasonal pressures. We identify the source,
            apply targeted eco-conscious treatments, and reinforce entry points so pests do not return.
          </p>
        </div>

        {/* 9-Card Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: "-60px"
      }} className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {pestItems.map((pest, index) => <motion.div key={pest.id} variants={itemVariants} className="h-full">
              <div data-index={index} className="h-full">
                <Card role="button" tabIndex={0} aria-haspopup="dialog" aria-label={`View treatment details for ${pest.name}`} onClick={() => setSelectedPest(pest)} onKeyDown={e => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                setSelectedPest(pest);
              }
            }} className="group h-full cursor-pointer border border-border bg-card shadow-xs transition-[border-color,box-shadow,transform] duration-200 hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary">
                  <CardContent className="flex flex-col p-6 sm:p-7">
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-4">
                        <div className="relative shrink-0">
                          <img src={pest.image.src} data-wvc-srcset={pest.image.srcset} data-wvc-sizes={pest.image.sizes} alt={pest.image.alt} loading="lazy" className="size-11 shrink-0 rounded-lg border border-border object-cover" />
                          {pest.id === "fleas" && <span className="absolute -bottom-1 -right-1 size-5 rounded-full bg-primary text-primary-foreground flex items-center justify-center shadow-sm">
                              <Camera className="size-3" />
                            </span>}
                        </div>
                        <h3 className="text-xl font-semibold tracking-tight text-foreground">
                          {pest.name}
                        </h3>
                      </div>
                      <ChevronRight className="size-5 shrink-0 text-muted-foreground/70 transition-transform duration-200 group-hover:translate-x-1 group-hover:text-primary" />
                    </div>

                    <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
                      {pest.description}
                    </p>
                  </CardContent>
                </Card>
              </div>
            </motion.div>)}
        </motion.div>

        {/* Support & Action Strip */}
        <div className="mt-14 flex flex-col items-center justify-between gap-6 rounded-xl border border-border bg-muted/50 p-6 sm:flex-row sm:p-8">
          <div className="text-center sm:text-left">
            <h4 className="text-base font-semibold text-foreground sm:text-lg">
              Not sure which pest you are dealing with?
            </h4>
            <p className="mt-1 text-sm text-muted-foreground">
              Send us a photo or call our local Auckland team for free honest advice.
            </p>
          </div>

          <div className="flex flex-wrap items-center justify-center gap-3">
            <Button asChild variant="secondary" className="h-10 px-5 text-sm font-semibold">
              <a href="tel:0221002103" className="inline-flex items-center gap-2">
                <Phone className="size-4" />
                <span>022 100 2103</span>
              </a>
            </Button>
            <Button type="button" onClick={() => {
            if (typeof window !== "undefined") {
              window.dispatchEvent(new CustomEvent("eco-open-quote-modal"));
            }
          }} className="h-10 px-5 text-sm font-semibold">
              <span>Book an inspection</span>
              <ArrowRight className="size-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Pest Details Dialog */}
      <Dialog open={Boolean(selectedPest)} onOpenChange={open => {
      if (!open) setSelectedPest(null);
    }}>
        <DialogContent className="max-w-lg border border-border bg-card p-6 sm:max-w-xl sm:p-8 max-h-[85vh] overflow-y-auto">
          {selectedPest && <>
              <div className="flex flex-col items-center gap-3 pb-5 mb-5 border-b border-border text-center">
                <Link to="/" aria-label="Eco Home Services Homepage">
                  <WvcLogo className="h-9 w-auto" />
                </Link>
                <span className="inline-flex items-center gap-1.5 rounded-full border border-primary/20 bg-secondary px-3.5 py-1 text-xs font-semibold uppercase tracking-wider text-secondary-foreground">
                  Get Rid of Pests, Professionally
                </span>
              </div>

              <div className="relative">
                <img src={selectedPest.image.src} data-wvc-srcset={selectedPest.image.srcset} data-wvc-sizes={selectedPest.image.sizes} alt={selectedPest.image.alt} className="h-44 w-full rounded-lg border border-border object-cover" />
                {selectedPest.id === "fleas" && <span className="absolute -bottom-1 -right-1 size-5 rounded-full bg-primary text-primary-foreground flex items-center justify-center shadow-sm">
                    <Camera className="size-3" />
                  </span>}
              </div>

              <DialogHeader className="text-left">
                <DialogTitle className="text-2xl font-bold tracking-tight text-foreground">
                  {selectedPest.name}
                </DialogTitle>
                <DialogDescription className="text-xs font-semibold uppercase tracking-wider text-primary">
                  Auckland Treatment Scope & Warning Signs
                </DialogDescription>
              </DialogHeader>

              <div className="mt-4">
                <p className="text-sm leading-relaxed text-muted-foreground sm:text-base">
                  {selectedPest.details}
                </p>
              </div>

              <div className="mt-6 flex flex-wrap items-center justify-end gap-3 pt-2">
                <Button asChild variant="secondary" className="h-10 px-5 text-sm font-semibold">
                  <a href="tel:0221002103" className="inline-flex items-center gap-2">
                    <Phone className="size-4" />
                    <span>Call 022 100 2103</span>
                  </a>
                </Button>
                <Button type="button" onClick={() => {
              setSelectedPest(null);
              if (typeof window !== "undefined") {
                window.dispatchEvent(new CustomEvent("eco-open-quote-modal"));
              }
            }} className="h-10 px-5 text-sm font-semibold">
                  <span>Get a Free Quote</span>
                  <ArrowRight className="size-4" />
                </Button>
              </div>
            </>}
        </DialogContent>
      </Dialog>
    </section>;
}